import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { QRCodeSVG } from 'qrcode.react'
import { Button, Card, StatusBadge, Alert, PageLayout, PageHeader } from '../components/ui'
import { AssignmentSection } from '../components/AssignmentSection'
import { COLORS } from '../styles/tokens/colors'

interface Survey {
  id: string
  title: string
  description: string
  public_id: string
  status: string
}

const SurveySharePage: React.FC = () => {
  const intl = useIntl()
  const { surveyId = '' } = useParams<{ surveyId: string }>()
  const [searchParams] = useSearchParams()
  const orgId = searchParams.get('org')
  useAuth()
  const [survey, setSurvey] = useState<Survey | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [responseCount, setResponseCount] = useState(0)

  const publicUrl = survey?.public_id
    ? `${window.location.origin}/survey/${survey.public_id}`
    : ''

  const isPublished = survey?.status === 'PUBLISHED'

  useEffect(() => {
    fetchSurvey()
    fetchResponseCount()
  }, [surveyId])

  const fetchSurvey = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      })
      if (response.ok) setSurvey(await response.json())
    } catch (error) {
      console.error('Error fetching survey:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchResponseCount = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(API_ENDPOINTS.surveys.responses(surveyId), {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      })
      if (response.ok) {
        const data = await response.json()
        setResponseCount(Array.isArray(data) ? data.length : 0)
      }
    } catch {
      // ignore
    }
  }

  const handleTogglePublish = async () => {
    if (!survey) return
    setToggling(true)
    try {
      const token = localStorage.getItem('access_token')
      const newStatus = isPublished ? 'DRAFT' : 'PUBLISHED'
      const response = await fetch(API_ENDPOINTS.surveys.byId(surveyId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ status: newStatus }),
      })
      if (response.ok) setSurvey(await response.json())
    } catch (error) {
      console.error('Error toggling publish:', error)
    } finally {
      setToggling(false)
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <PageLayout maxWidth="md">
      <PageHeader
        title={survey?.title || intl.formatMessage({ id: 'share.surveyFallbackTitle' })}
        subtitle={survey?.description || undefined}
        backTo={orgId ? `/organizations/${orgId}?tab=surveys` : '/dashboard'}
        backLabel={
          orgId
            ? intl.formatMessage({ id: 'share.backToOrganization' })
            : intl.formatMessage({ id: 'share.backToDashboard' })
        }
        actions={
          <div className="flex gap-2">
            <Link to={`/surveys/${surveyId}/edit`}>
              <Button variant="primary" size="sm">
                <FormattedMessage id="share.editSurvey" />
              </Button>
            </Link>
            <Link to={`/surveys/${surveyId}/preview`}>
              <Button variant="outline" size="sm">
                <FormattedMessage id="share.preview" />
              </Button>
            </Link>
            <Link to={`/surveys/${surveyId}/responses`}>
              <Button variant="outline" size="sm">
                <FormattedMessage id="share.responsesCount" values={{ count: responseCount }} />
              </Button>
            </Link>
          </div>
        }
      />

      {/* Status + Toggle */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-muted">
              <FormattedMessage id="share.statusLabel" />
            </span>
            <StatusBadge status={isPublished ? 'PUBLISHED' : 'DRAFT'} />
            {isPublished && (
              <span className="text-sm text-foreground-muted">
                <FormattedMessage id="share.responsesCollected" values={{ count: responseCount }} />
              </span>
            )}
          </div>
          <Button
            variant={isPublished ? 'outline' : 'primary'}
            onClick={handleTogglePublish}
            loading={toggling}
          >
            {toggling ? (
              <FormattedMessage id="share.updating" />
            ) : isPublished ? (
              <FormattedMessage id="share.unpublish" />
            ) : (
              <FormattedMessage id="share.publishSurvey" />
            )}
          </Button>
        </div>
      </Card>

      {/* Share section -- only when published */}
      {isPublished && (
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Share Link */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-3">
                <FormattedMessage id="share.shareLinkHeading" />
              </h3>
              <div className="flex mb-2">
                <input
                  type="text"
                  readOnly
                  value={publicUrl}
                  data-testid="public-survey-url"
                  className="flex-1 block w-full rounded-l-lg border border-border px-3 py-2 text-sm bg-background-alt text-foreground"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 border border-l-0 border-border rounded-r-lg text-sm font-medium transition-colors ${
                    copied
                      ? 'bg-success-light text-success border-success'
                      : 'bg-surface text-foreground hover:bg-background-alt'
                  }`}
                >
                  {copied ? (
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <FormattedMessage id="share.copied" />
                    </span>
                  ) : (
                    <FormattedMessage id="share.copy" />
                  )}
                </button>
              </div>
              <p className="text-xs text-foreground-muted">
                <FormattedMessage id="share.shareLinkHelp" />
              </p>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="bg-surface p-3 rounded-lg border border-border">
                <QRCodeSVG
                  value={publicUrl}
                  size={140}
                  level="M"
                  bgColor={COLORS.surface}
                  fgColor={COLORS.primary}
                />
              </div>
              <p className="text-xs text-foreground-muted mt-2">
                <FormattedMessage id="share.scanToOpen" />
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Draft message */}
      {!isPublished && (
        <Alert variant="warning" className="mb-6">
          <p className="font-medium">
            <FormattedMessage id="share.notPublishedTitle" />
          </p>
          <p className="text-sm mt-1">
            <FormattedMessage id="share.notPublishedHelp" />
          </p>
        </Alert>
      )}

      {/* Assignment section — only in org context */}
      {orgId && <AssignmentSection surveyId={surveyId} orgId={orgId} />}
    </PageLayout>
  )
}

export default SurveySharePage
